// H-16b: the round's Activity view.
//
// Who did what and when, with the reason they gave, newest first. It is the
// owner's read-only window onto the audit log (L-8), and it is read-only on
// purpose: an append-only record of inputs, outputs and human decisions is what
// the EU AI Act's logging duty asks for, and what proves a person decided. It
// stays available after the round closes (H-16c).
//
// The model and prompt versions are deliberately NOT here. They are in the
// explanation export and in a Platform Admin's full log export, which is where
// a regulator's question gets answered, not in a hiring manager's screen.

import React from 'react';
import { createPortal } from 'react-dom';
import { Box, Typography } from '@mui/material';
import { Bone, fadeInSx } from '../../../../components/ui/DashboardSkeletons';
import { useHiringActivity } from '../../../../queries/useHiring';
import {
  ActionButton,
  BODY,
  BRANDY,
  FONT,
  LABEL,
  LINE,
  TEXT,
  TEXT_2,
  TEXT_3,
  WELL
} from '../capability-setup/designSystem';
import { exportActivityCsv } from './hiringExport';
import { Empty, when } from './hiringParts';
import type { HiringActivityEntry, HiringCandidateView, HiringRoundView } from '../../../../api/hiring';

/** What each logged action reads as. Anything unmapped falls back to its own id. */
const WORDS: Record<string, string> = {
  'round.created': 'created the round',
  'round.assessmentBuilt': 'had Elliott draft the assessment',
  'round.updated': 'changed the round',
  'round.opened': 'opened the round',
  'round.closed': 'closed the round',
  'round.reopened': 'reopened the round',
  'round.ranked': 'changed the ranking',
  'owner.acknowledged': 'confirmed the "Using AI in Hiring" notice',
  'candidate.invited': 'invited',
  'candidate.inviteResent': 'was sent a reminder',
  'candidate.inviteRevoked': 'had their invite revoked',
  'candidate.residenceGiven': 'gave their country of residence',
  'candidate.residenceBlocked': 'could not start: their location is not supported yet',
  'candidate.noticeAcknowledged': 'acknowledged the notice',
  'candidate.onboarded': 'filled in the onboarding fields',
  'candidate.cvUploaded': 'uploaded their CV',
  'candidate.started': 'started the assessment',
  'candidate.attemptGraded': 'had an attempt graded by Elliott',
  'candidate.clarificationAsked': 'asked Elliott a question',
  'candidate.quizAnswered': 'answered a quiz question',
  'candidate.analysisWritten': 'had their analysis written by Elliott',
  'candidate.completed': 'completed the assessment',
  'candidate.viewed': 'opened the profile of',
  'candidate.noted': 'made a note on',
  'candidate.noteEdited': 'edited a note on',
  'candidate.noteDeleted': 'deleted a note on',
  'candidate.removed': 'removed from the round',
  'candidate.restored': 'restored to the round',
  'candidate.deleted': 'deleted entirely',
  'candidate.hired': 'hired',
  'candidate.workEmailCorrected': 'corrected the company email of',
  'candidate.explanationExported': 'downloaded the explanation for',
  'candidate.purged': 'was deleted, their retention period having ended',
  'candidate.pseudonymised': 'had their personal data removed from the log'
};

const RoundActivity: React.FC<{
  round: HiringRoundView;
  candidates: HiringCandidateView[];
  /** Where its Export goes: the round's header. */
  exportSlot: HTMLElement | null;
}> = ({ round, candidates, exportSlot }) => {
  const activity = useHiringActivity(round.id);
  const nameOf = (id: string) => candidates.find((c) => c.id === id)?.name || '';
  const whoDid = (entry: HiringActivityEntry) =>
    entry.actorKind === 'candidate'
      ? nameOf(entry.candidateId) || 'The candidate'
      : entry.actorKind === 'system'
      ? 'Dosen'
      : entry.actorName || 'A Team Owner';
  const whatHappened = (entry: HiringActivityEntry) => WORDS[entry.action] || entry.action;
  const whoAbout = (entry: HiringActivityEntry) =>
    entry.actorKind === 'owner' && entry.candidateId ? nameOf(entry.candidateId) : '';
  const entries = activity.data || [];

  return (
    <>
      {exportSlot &&
        createPortal(
          <ActionButton
            disabled={entries.length === 0}
            reason={entries.length === 0 ? 'There is no activity to export yet' : undefined}
            onClick={() =>
              exportActivityCsv(
                round,
                entries.map((entry) => [
                  when(entry.at),
                  whoDid(entry),
                  whatHappened(entry),
                  whoAbout(entry),
                  entry.rationale || ''
                ])
              )
            }
          >
            Export
          </ActionButton>,
          exportSlot
        )}
      {activity.isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', ...fadeInSx }}>
          <Bone height={40} />
          <Bone height={40} />
          <Bone height={40} />
        </Box>
      ) : entries.length === 0 ? (
        <Empty>Nothing has happened in this round yet.</Empty>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {entries.map((entry, index) => {
            const who = whoDid(entry);
            const what = whatHappened(entry);
            const about = whoAbout(entry) ? ` ${whoAbout(entry)}` : '';
            return (
              <Box
                key={`${entry.at}-${index}`}
                sx={{
                  p: '10px 12px',
                  bgcolor: WELL,
                  border: `1px solid ${LINE}`,
                  borderRadius: '4px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}
              >
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT }}>
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {who}
                    </Box>{' '}
                    {what}
                    {about}
                  </Typography>
                  <Typography sx={{ ...BODY, fontSize: 11.5, color: TEXT_3 }}>{when(entry.at)}</Typography>
                </Box>
                {/* L-3: the reason a person gave, kept with the record. */}
                {entry.rationale && (
                  <Box sx={{ borderLeft: `2px solid ${BRANDY}`, pl: '10px' }}>
                    <Typography sx={{ ...LABEL, color: TEXT_3, mb: '2px' }}>Their reason</Typography>
                    <Typography sx={{ ...BODY, color: TEXT_2 }}>{entry.rationale}</Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
};

export default RoundActivity;
